import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, FormControlLabel, Checkbox, Grid, FormControl, Select, MenuItem, Autocomplete, TextField, Tooltip, Button, IconButton, InputAdornment, Stack, AutocompleteProps, InputProps, OutlinedInputProps, FilledInputProps } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { cloneDeep } from "lodash";
import { FormValue } from "./views/FieldForm";
import { FieldSettingsOptions, IntegrationFieldConfig } from "../../../../../../shell/services/types";
import { convertDropdownValue } from "../../utils";
import { withCursorPosition } from "../../../../../../shell/components/withCursorPosition";
import { Currency } from "../../../../../../shell/components/FieldTypeCurrency/currencies";
import { IntegrationFieldConfigure } from "shell/components/FieldTypeIntegration";
const TextFieldWithCursorPosition = withCursorPosition(TextField);
export type Validation = "length" | "required" | "unique";
export type FieldNames = "name" | "label" | "description" | "required" | "list" | "options" | "relatedModelZUID" | "relatedFieldZUID" | "group_id" | "limit" | "tooltip" | "defaultValue" | "maxCharLimit" | "minCharLimit" | "regexMatchPattern" | "regexMatchErrorMessage" | "regexRestrictPattern" | "regexRestrictErrorMessage" | "minValue" | "maxValue" | "currency" | "fileExtensions" | "fileExtensionsErrorMessage" | "integrationFieldConfig";
type FieldType = "input" | "checkbox" | "dropdown" | "autocomplete" | "options" | "toggle_options" | "config";
type InputType = "text" | "number";
export interface InputField {
  name: FieldNames;
  type: FieldType;
  label: string;
  required: boolean;
  gridSize: number;
  subLabel?: string;
  fullWidth?: boolean;
  multiline?: boolean;
  maxLength?: number;
  placeholder?: string;
  tooltip?: string;
  validate?: Validation[];
  inputType?: InputType;
  autoFocus?: boolean;
}
export interface DropdownOptions {
  label: string;
  value: string;
}
export type AutocompleteConfig = {
  inputProps?: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps>;
  maxHeight?: number;
};
type FieldFormInputProps = {
  fieldConfig: InputField;
  errorMsg?: string | [string, string][];
  onDataChange: ({
    inputName,
    value
  }: {
    inputName: string;
    value: FormValue;
  }) => void;
  prefillData?: FormValue;
  dropdownOptions?: DropdownOptions[] | Currency[];
  disabled?: boolean;
  autocompleteConfig?: AutocompleteConfig;
  integrationFieldConfig?: IntegrationFieldConfig;
  isUpdateField?: boolean;
  startAdornment?: React.ReactNode;
} & Pick<AutocompleteProps<DropdownOptions | Currency, false, false, false, "div">, "renderOption" | "filterOptions">;
export const FieldFormInput = ({
  fieldConfig,
  errorMsg,
  onDataChange,
  prefillData,
  dropdownOptions,
  disabled,
  renderOption,
  filterOptions,
  autocompleteConfig,
  integrationFieldConfig,
  isUpdateField,
  startAdornment
}: FieldFormInputProps) => {
  const {
    t
  } = useTranslation();
  const options = fieldConfig.type === "options" || fieldConfig.type === "toggle_options" && prefillData ? prefillData as FieldSettingsOptions[] : [];
  const handleOptionValueChanged = (newKeyValueData: {
    [key: string]: string;
  }, index: number) => {
    if (!options[index]) {
      return;
    }
    const localOptionsCopy = cloneDeep(options);
    localOptionsCopy.splice(index, 1, newKeyValueData);
    onDataChange({
      inputName: fieldConfig.name,
      value: [...localOptionsCopy]
    });
  };
  const handleAddNewOption = () => {
    onDataChange({
      inputName: fieldConfig.name,
      value: [...options, {
        "": ""
      }]
    });
  };
  const handleDeleteOption = (index: number) => {
    if (!options[index]) {
      return;
    }
    const localOptionsCopy = cloneDeep(options);
    localOptionsCopy.splice(index, 1);
    onDataChange({
      inputName: fieldConfig.name,
      value: [...localOptionsCopy]
    });
  };
  const InputTextField = fieldConfig.name === "name" ? TextFieldWithCursorPosition : TextField;
  return <Grid size={fieldConfig.gridSize} sx={{
    pl: 2.5
  }} data-sentry-element="Grid" data-sentry-component="FieldFormInput" data-sentry-source-file="FieldFormInput.tsx">
      {fieldConfig.type === "checkbox" && <FormControlLabel control={<Checkbox name={fieldConfig.name} required={fieldConfig.required} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      onDataChange({
        inputName: fieldConfig.name,
        value: e.target.checked
      });
    }} checked={Boolean(prefillData)} size="small" />} label={<Stack>
              <Typography variant="body2" fontWeight={600}>
                {fieldConfig.label}
              </Typography>
              {fieldConfig.subLabel && <Typography variant="body3" color="text.secondary">
                  {fieldConfig.subLabel}
                </Typography>}
            </Stack>} sx={{
      alignItems: "flex-start"
    }} />}

      {fieldConfig.type === "dropdown" && <FormControl fullWidth size="small">
          <Typography variant="body2" mb={0.5} fontWeight={600}>
            {fieldConfig.label}
          </Typography>
          <Select value={prefillData || ""} displayEmpty onChange={(e: SelectChangeEvent) => {
        onDataChange({
          inputName: fieldConfig.name,
          value: e.target.value
        });
      }}>
            <MenuItem disabled value="">
              {fieldConfig.placeholder}
            </MenuItem>
            {dropdownOptions?.map(({
          label,
          value
        }) => <MenuItem key={value} value={value}>
                {label}
              </MenuItem>)}
          </Select>
        </FormControl>}

      {fieldConfig.type === "autocomplete" && <>
          <Stack flexDirection="row" alignItems="center" mb={0.5} height={18}>
            <Typography variant="body2" fontWeight={600}>
              {fieldConfig.label}
            </Typography>
            {fieldConfig.tooltip && <Tooltip placement="right" title={fieldConfig.tooltip}>
                <InfoRoundedIcon sx={{
            ml: 1,
            width: "12px",
            height: "12px"
          }} color="action" />
              </Tooltip>}
          </Stack>
          <Autocomplete data-cy={`Autocomplete_${fieldConfig.name}`} size="small" disabled={disabled} value={dropdownOptions.find(option => option.value === prefillData) || null} options={dropdownOptions} renderInput={params => <TextField {...params} placeholder={fieldConfig.placeholder} hiddenLabel autoFocus={fieldConfig.autoFocus} error={!!errorMsg} helperText={errorMsg} InputProps={{
        ...params.InputProps,
        ...autocompleteConfig?.inputProps
      }} />} isOptionEqualToValue={(option, value) => option.value === value.value} onChange={(_, newValue: DropdownOptions) => {
        onDataChange({
          inputName: fieldConfig.name,
          value: newValue?.value || ""
        });
      }} sx={{
        "& .MuiOutlinedInput-root": {
          height: "40px"
        }
      }} renderOption={renderOption} filterOptions={filterOptions} slotProps={{
        paper: {
          sx: {
            "& .MuiAutocomplete-listbox": {
              maxHeight: autocompleteConfig?.maxHeight || "40vh",
              boxSizing: "border-box"
            }
          }
        }
      }} />
          {prefillData && !dropdownOptions.find(option => option.value === prefillData) && <Typography noWrap color="error.dark" variant="body2" mt={0.5}>
                {fieldConfig.name === "group_id" && t("schema.folderDeletedError")}
                {fieldConfig.name === "relatedModelZUID" && t("schema.relatedModelDeletedError")}
              </Typography>}
        </>}

      {fieldConfig.type === "input" && <>
          <Stack flexDirection="row" alignItems="center" mb={!!fieldConfig.subLabel ? 0 : 0.5} height={18}>
            <Typography component="span" variant="body2" fontWeight={600}>
              {fieldConfig.label} {fieldConfig.required && "*"}
            </Typography>
            {fieldConfig.label?.toLowerCase().includes("description") && <Typography component="span" variant="body2" color="text.secondary" sx={{
          whiteSpace: "pre"
        }}>
                {" ("}
                {t("schema.optionalLabel")}
                {")"}
              </Typography>}
            {fieldConfig.tooltip && <Tooltip placement="right" title={fieldConfig.tooltip}>
                <InfoRoundedIcon sx={{
            ml: 1,
            width: "12px",
            height: "12px"
          }} color="action" />
              </Tooltip>}
          </Stack>
          {fieldConfig.subLabel && <Typography component="p" variant="body3" color="text.secondary" mb={0.5}>
              {fieldConfig.subLabel}
            </Typography>}
          <InputTextField autoFocus={fieldConfig.autoFocus} data-cy={`FieldFormInput_${fieldConfig.name}`} name={fieldConfig.name} required={fieldConfig.required} fullWidth={fieldConfig.fullWidth} multiline={fieldConfig.multiline} minRows={fieldConfig.multiline && 2} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        onDataChange({
          inputName: fieldConfig.name,
          value: e.target.value
        });
      }} value={prefillData || ""} error={Boolean(errorMsg)} helperText={errorMsg && <Box component="span" data-cy={`ErrorMsg_${fieldConfig.name}`}>
                  {errorMsg}
                </Box>} type={fieldConfig.inputType || "text"} inputProps={{
        min: 1
      }} InputProps={startAdornment ? {
        startAdornment: <InputAdornment position="start" sx={{
          "&.MuiInputAdornment-positionStart": {
            mr: 0
          }
        }}>
                        {startAdornment}
                      </InputAdornment>
      } : undefined} />
        </>}
      {(fieldConfig.type === "options" || fieldConfig.type === "toggle_options") && <>
          <Typography variant="body2" mb={2} fontWeight={600}>
            {fieldConfig.label}
          </Typography>
          {options?.map((option, index) => {
        let key,
          value = "";
        Object.entries(option).forEach(([k, v]) => {
          key = k;
          value = v;
        });
        return <KeyValueInput id={index} key={index} optionKey={key} optionValue={value} errorMsg={errorMsg[index] as [string, string]} onOptionChange={newKeyValueData => handleOptionValueChanged(newKeyValueData, index)} onDeleteOption={() => handleDeleteOption(index)} isDeletable={fieldConfig.type === "options"} disabledFields={fieldConfig.type === "toggle_options" ? ["key"] : []} />;
      })}
          {fieldConfig.type === "options" && <Button data-cy="DropdownAddOption" variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleAddNewOption} size="small">
              {t("schema.addOption")}
            </Button>}
        </>}
      {fieldConfig.type === "config" && <IntegrationFieldConfigure integrationFieldConfig={integrationFieldConfig} onChange={value => onDataChange({
      inputName: fieldConfig.name,
      value
    })} isUpdate={isUpdateField} error={errorMsg} />}
    </Grid>;
};
interface KeyValueInputProps {
  id: number;
  optionKey: string;
  optionValue: string;
  errorMsg?: [string, string];
  onOptionChange: (newKeyValueData: {
    [key: string]: string;
  }) => void;
  onDeleteOption: () => void;
  isDeletable: boolean;
  disabledFields?: string[];
}
const KeyValueInput = ({
  id,
  optionKey,
  optionValue,
  errorMsg,
  onOptionChange,
  onDeleteOption,
  isDeletable,
  disabledFields
}: KeyValueInputProps) => {
  const {
    t
  } = useTranslation();
  const handleDataChanged = (type: string, value: string) => {
    if (type === "key") {
      onOptionChange({
        [convertDropdownValue(value) || ""]: optionValue
      });
    }
    if (type === "value") {
      if (disabledFields.includes("key")) {
        onOptionChange({
          [optionKey]: value
        });
      } else {
        // When the value is changed, automatically change the key as well
        onOptionChange({
          [convertDropdownValue(value) || ""]: value
        });
      }
    }
  };
  const [valueErrorMsg, labelErrorMsg] = errorMsg || [];
  return <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} data-sentry-element="Box" data-sentry-component="KeyValueInput" data-sentry-source-file="FieldFormInput.tsx">
      <Box display="flex" gap={1} width="480px" data-sentry-element="Box" data-sentry-source-file="FieldFormInput.tsx">
        <TextField data-cy={`OptionLabel_${id}`} name="value" required fullWidth placeholder={t("schema.enterLabel")} value={optionValue || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        handleDataChanged("value", e.target?.value);
      }} helperText={<Box component="span" data-cy={`OptionLabelErrorMsg_${id}`}>
              {labelErrorMsg}
            </Box>} error={Boolean(labelErrorMsg)} disabled={disabledFields.includes("value")} data-sentry-element="TextField" data-sentry-source-file="FieldFormInput.tsx" />
        <TextFieldWithCursorPosition data-cy={`OptionValue_${id}`} name="key" required fullWidth placeholder={t("schema.enterValue")} value={optionKey || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        handleDataChanged("key", e.target?.value);
      }} helperText={<Box component="span" data-cy={`OptionValueErrorMsg_${id}`}>
              {valueErrorMsg}
            </Box>} error={Boolean(valueErrorMsg)} disabled={disabledFields.includes("key")} data-sentry-element="TextFieldWithCursorPosition" data-sentry-source-file="FieldFormInput.tsx" />
      </Box>
      {isDeletable && <IconButton data-cy={`DeleteOption_${id}`} size="small" onClick={onDeleteOption}>
          <DeleteRoundedIcon />
        </IconButton>}
    </Box>;
};